import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Video, MessageCircle, FileText, BookOpen, Sparkles, Newspaper } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import ProductsTab from '@/components/admin/ProductsTab';
import VideosTab from '@/components/admin/VideosTab';
import FAQsTab from '@/components/admin/FAQsTab';
import ArticlesTab from '@/components/admin/ArticlesTab';
import BookOrdersTab from '@/components/admin/BookOrdersTab';
import TruffleOrdersTab from '@/components/admin/TruffleOrdersTab';
import NewsTab from '@/components/admin/NewsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [products, setProducts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [articles, setArticles] = useState([]);
  const [bookOrders, setBookOrders] = useState([]);
  const [truffleOrders, setTruffleOrders] = useState([]);
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, videosRes, faqsRes, articlesRes, ordersRes, truffleOrdersRes, newsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/videos`),
          axios.get(`${API}/faqs`),
          axios.get(`${API}/articles`),
          axios.get(`${API}/admin/book-orders`),
          axios.get(`${API}/admin/truffle-orders`),
          axios.get(`${API}/admin/news`)
        ]);
        setProducts(productsRes.data);
        setVideos(videosRes.data);
        setFaqs(faqsRes.data);
        setArticles(articlesRes.data);
        setBookOrders(ordersRes.data);
        setTruffleOrders(truffleOrdersRes.data);
        setNewsPosts(newsRes.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="admin-page">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="section-header mb-4">Admin Panel</h1>
          <p className="section-subheader">Manage your content, products, and orders</p>
        </motion.div>

        <Tabs defaultValue="news" className="space-y-8">
          <TabsList className="grid grid-cols-4 md:grid-cols-7 gap-2 h-auto bg-muted/50 p-2 rounded-xl">
            <TabsTrigger value="news" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              <Newspaper className="w-4 h-4" />
              <span className="hidden md:inline">News</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-white">
              <Package className="w-4 h-4" />
              <span className="hidden md:inline">Products</span>
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-white">
              <Video className="w-4 h-4" />
              <span className="hidden md:inline">Videos</span>
            </TabsTrigger>
            <TabsTrigger value="faqs" className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-white">
              <MessageCircle className="w-4 h-4" />
              <span className="hidden md:inline">FAQs</span>
            </TabsTrigger>
            <TabsTrigger value="articles" className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-white">
              <FileText className="w-4 h-4" />
              <span className="hidden md:inline">Articles</span>
            </TabsTrigger>
            <TabsTrigger value="book-orders" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              <BookOpen className="w-4 h-4" />
              <span className="hidden md:inline">Books</span>
            </TabsTrigger>
            <TabsTrigger value="truffle-orders" className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-white">
              <Sparkles className="w-4 h-4" />
              <span className="hidden md:inline">Truffles</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="news">
            <NewsTab posts={newsPosts} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="products">
            <ProductsTab products={products} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="videos">
            <VideosTab videos={videos} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="faqs">
            <FAQsTab faqs={faqs} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="articles">
            <ArticlesTab articles={articles} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="book-orders">
            <BookOrdersTab orders={bookOrders} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="truffle-orders">
            <TruffleOrdersTab orders={truffleOrders} onRefresh={fetchData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
